import { Spacer } from "@hackthenorth/north";
import { Button, Checkbox, Label } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useGetFilesQuery } from "src/api/files/getFiles.generated";
import { Dropzone, FileLink, IMAGE_FILE_TYPES } from "src/shared/components";
import Modal from "src/shared/components/Modal";
import { convertToFileAnswers } from "src/shared/utils/hackerapi";
import type { HackerAPIFile } from "src/shared/utils/hackerapi";
import {
  PerkError,
  PerkTextArea,
  PerkTextInput,
} from "src/views/sponsor/perks/components/Perk/components";
import {
  TextDescription,
  TextInputTitle,
} from "src/views/sponsor/perks/components/Perk/TextComponents";
import { useFileDropzone } from "src/views/sponsor/perks/components/Perk/useFileDropzone";

import {
  SWAG_BAG_ARRIVAL_DEADLINE,
  GENERAL_EVENT_SWAG_ARRIVAL_DEADLINE,
} from "../constants";
import { ShipmentStage, Shipment } from "../types";
import {
  createEmptyShipment,
  emptyShipment,
  formatShipmentDate,
  getShipmentLateWarning,
  getShipmentStageColor,
  getShipmentStageLabel,
  REQUIRED_SHIPMENT_FIELDS,
} from "../utils";

import { ShipmentWarningModal } from "./ShipmentWarningModal";
import { TrackingCodeInput } from "./TrackingCodeInput";

export const ShipmentModal = ({
  isOpen,
  shipment = emptyShipment,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  shipment?: Shipment;
  onClose: () => void;
  onSave: (shipment: Shipment) => void;
}) => {
  const isCreating = shipment === emptyShipment;

  const [currShipment, setCurrShipment] = useState(() =>
    isCreating ? createEmptyShipment() : shipment
  );

  // validationErrors is a map that keeps track of which required fields are not filled in
  // (not filled in = true)
  const [validationErrors, setValidationErrors] = useState<{
    description?: boolean;
    total_boxes?: boolean;
    eta?: boolean;
    date_shipped?: boolean;
  }>({});
  const [pendingSubmit, setPendingSubmit] = useState<{
    stage?: Shipment["stage"];
    warnings: string[];
  } | null>(null);
  const [receiptFiles, setReceiptFiles] = useState<HackerAPIFile[]>([]);

  // these are the receipt file IDs pulled straight from the shipment JSON.
  // these are only stored to convert them from Number[] to HackerAPIFile[]
  // on modal open
  const initialReceiptFileIds = shipment.shipping_receipt_file_id;

  const { data: initialReceiptFilesData } = useGetFilesQuery({
    variables: { fileIds: initialReceiptFileIds },
    skip: !isOpen || initialReceiptFileIds.length === 0,
  });

  const {
    dropzoneInputRef,
    fetchedFiles,
    error: fileError,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    receiptFiles,
    (files) => {
      setReceiptFiles(files ?? []);
      setCurrShipment((prev) => ({
        ...prev,
        shipping_receipt_file_id: convertToFileAnswers(files),
      }));
    },
    15
  );

  useEffect(() => {
    if (isOpen) {
      setCurrShipment(
        shipment === emptyShipment ? createEmptyShipment() : shipment
      );
      setValidationErrors({});
      setPendingSubmit(null);

      if (initialReceiptFileIds.length === 0) {
        setReceiptFiles([]);
        return;
      }

      // converting the FileIDs (Number[]) to Files (HackerAPIFile[])
      if (initialReceiptFilesData?.files) {
        setReceiptFiles(
          initialReceiptFilesData.files.map((file) => ({
            id: file.id,
            name: file.name,
            mime_type: file.mime_type,
            ...file.download,
          }))
        );
      }
    }
  }, [shipment, isOpen, initialReceiptFileIds, initialReceiptFilesData]);

  const isDraft = currShipment.stage === "draft";
  const canMarkAsShipped = isCreating || isDraft;

  const etaLateReason = currShipment.eta
    ? getShipmentLateWarning(currShipment)
    : null;
  const update = <K extends keyof Shipment>(field: K, value: Shipment[K]) => {
    setCurrShipment((prev) => ({ ...prev, [field]: value }));
    if (field in validationErrors) {
      setValidationErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // checks for stricly required fields
  const validate = (): boolean => {
    const errors: typeof validationErrors = {};

    for (const field of REQUIRED_SHIPMENT_FIELDS) {
      if (!currShipment[field]) errors[field] = true;
    }
    if (!currShipment.total_boxes || currShipment.total_boxes < 1) {
      errors.total_boxes = true;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const save = (stage?: ShipmentStage) => {
    onSave(stage ? { ...currShipment, stage } : currShipment);
  };

  const handleSaveAsDraft = () => {
    save("draft");
  };

  // checks for strongly recommended fields/conditions, but that aren't strictly required
  const collectWarnings = (): string[] => {
    const warnings: string[] = [];
    if (currShipment.tracking_codes.length !== currShipment.total_boxes) {
      const codes = currShipment.tracking_codes.length;
      const boxes = currShipment.total_boxes;
      warnings.push(
        `You have ${codes} tracking code${
          codes === 1 ? "" : "s"
        } but ${boxes} total box${boxes === 1 ? "" : "es"}.`
      );
    }
    const lateReason = getShipmentLateWarning(currShipment);
    if (lateReason) warnings.push(lateReason);
    return warnings;
  };

  const handleSubmit = (stage?: ShipmentStage) => {
    if (!validate()) return;
    const warnings = collectWarnings();
    if (warnings.length > 0) {
      setPendingSubmit({ stage, warnings });
      return;
    }
    save(stage);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isCreating ? "New shipment" : "Shipment details"}
        width="840px"
        height="75vh"
      >
        {isCreating ? (
          <>
            <TextDescription>
              Add a new shipment to track boxes you are sending to Hack the
              North.
            </TextDescription>
            <Spacer height={16} />
          </>
        ) : (
          <>
            <Label color={getShipmentStageColor(currShipment.stage)} size="sm">
              {getShipmentStageLabel(currShipment.stage)}
            </Label>
            <Spacer height={16} />
          </>
        )}

        {/* organizer-entered fields only show up once the user has submitted a shipment */}
        {!canMarkAsShipped && (
          <>
            {currShipment.boxes_received > 0 && (
              <>
                <TextInputTitle>Boxes arrived</TextInputTitle>
                <Spacer height={4} />
                <TextDescription>
                  {currShipment.boxes_received} / {currShipment.total_boxes}
                </TextDescription>
                <Spacer height={16} />
              </>
            )}
            {currShipment.arrival_date && (
              <>
                <TextInputTitle>Arrived on</TextInputTitle>
                <Spacer height={4} />
                <TextDescription>
                  {formatShipmentDate(currShipment.arrival_date)}
                </TextDescription>
                <Spacer height={16} />
              </>
            )}
            {currShipment.organizer_shipping_notes && (
              <>
                <TextInputTitle>Organizer notes</TextInputTitle>
                <Spacer height={4} />
                <TextDescription>
                  {currShipment.organizer_shipping_notes}
                </TextDescription>
                <Spacer height={16} />
              </>
            )}
          </>
        )}

        <PerkTextInput
          title="Box description*"
          value={currShipment.description}
          placeholder="e.g. Branded water bottles and stickers for HTN attendees"
          isReadOnly={false}
          onChange={(e) => update("description", e.target.value)}
          error={
            validationErrors.description ? (
              <>Please enter a box description</>
            ) : undefined
          }
        />
        <Spacer height={16} />
        <PerkTextInput
          title="Total boxes*"
          value={
            currShipment.total_boxes ? String(currShipment.total_boxes) : ""
          }
          placeholder="0"
          isReadOnly={false}
          type="text"
          onChange={(e) =>
            update("total_boxes", Number.parseInt(e.target.value) || 0)
          }
          error={
            validationErrors.total_boxes ? (
              <>Please enter the total number of boxes</>
            ) : undefined
          }
        />
        <Spacer height={16} />
        <PerkTextInput
          title="Date shipped*"
          value={currShipment.date_shipped}
          placeholder=""
          isReadOnly={false}
          type="date"
          onChange={(e) => update("date_shipped", e.target.value)}
          error={
            validationErrors.date_shipped ? (
              <>Please enter the date shipped</>
            ) : undefined
          }
        />
        <Spacer height={16} />
        <PerkTextInput
          title="Estimated arrival (ETA)*"
          value={currShipment.eta}
          placeholder=""
          isReadOnly={false}
          type="date"
          onChange={(e) => update("eta", e.target.value)}
          error={
            validationErrors.eta ? (
              <>Please enter an estimated arrival date</>
            ) : undefined
          }
        />
        <Spacer height={8} />
        {etaLateReason && (
          <>
            <LateWarningText>{etaLateReason}</LateWarningText>
            <Spacer height={8} />
          </>
        )}

        <TextDescription>
          For items intended to go in hacker&apos;s swag bags, all items must be
          received no later than {formatShipmentDate(SWAG_BAG_ARRIVAL_DEADLINE)}
          . For all other items, we must receive them by{" "}
          {formatShipmentDate(GENERAL_EVENT_SWAG_ARRIVAL_DEADLINE)}.
        </TextDescription>

        <Spacer height={16} />
        <TrackingCodeInput
          title="Tracking codes"
          codes={currShipment.tracking_codes}
          onChange={(codes) => update("tracking_codes", codes)}
        />
        <Spacer height={16} />
        <TextInputTitle>Shipping receipt</TextInputTitle>
        <Spacer height={8} />
        <TextDescription>
          Upload your shipping receipt or proof of shipment (PDF or image).
        </TextDescription>
        <Spacer height={16} />
        <Dropzone
          max={15}
          acceptedFileTypes={IMAGE_FILE_TYPES}
          onFilesUpload={addFiles}
          onError={onFileError}
          ref={dropzoneInputRef}
          onBlur={() => setDropzoneErrorMessage("")}
        />
        {fetchedFiles?.map((file, index) => (
          <React.Fragment key={file.id ?? index}>
            <Spacer height={16} />
            <FileLink file={file} deleteFile={() => deleteFile(index)} />
          </React.Fragment>
        ))}
        {fileError && (
          <>
            <Spacer height={8} />
            <PerkError error={fileError} />
          </>
        )}
        <Spacer height={16} />
        <TextInputTitle>Swag bags</TextInputTitle>
        <Spacer height={8} />
        <Checkbox
          checked={currShipment.include_in_swag_bags}
          label="Include in swag bags"
          size={18}
          onChange={() =>
            update("include_in_swag_bags", !currShipment.include_in_swag_bags)
          }
        />
        <Spacer height={8} />
        <TextDescription>
          Our swag bags are distributed to all hackers upon registration. If
          you&apos;d like your items included, please provide 1,300 units of
          each item. To ensure your swag is included, all items must be received
          no later than {formatShipmentDate(SWAG_BAG_ARRIVAL_DEADLINE)}.
        </TextDescription>
        <Spacer height={16} />
        <PerkTextArea
          title="Sponsor shipping notes"
          value={currShipment.sponsor_shipping_notes}
          placeholder="e.g. Boxes are labeled 1–12. Please keep upright."
          isReadOnly={false}
          wordLimit={100}
          onChange={(e) => update("sponsor_shipping_notes", e.target.value)}
        />

        <ActionsFooter>
          {canMarkAsShipped ? (
            <>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button color="secondary" onClick={handleSaveAsDraft}>
                Save as draft
              </Button>
              <Button color="primary" onClick={() => handleSubmit("shipped")}>
                Mark as shipped
              </Button>
            </>
          ) : (
            <>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button color="primary" onClick={() => handleSubmit()}>
                Save changes
              </Button>
            </>
          )}
        </ActionsFooter>
      </Modal>
      <ShipmentWarningModal
        isOpen={pendingSubmit !== null}
        warnings={pendingSubmit?.warnings ?? []}
        onCancel={() => setPendingSubmit(null)}
        onConfirm={() => {
          save(pendingSubmit?.stage);
          setPendingSubmit(null);
        }}
      />
    </>
  );
};

const LateWarningText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
  font-weight: 500;
`;

const ActionsFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin: 24px -36px -36px;
  padding: 24px 40px;
  background: white;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;
